// src/providers/dataProvider.ts
import { buildSummaryQuery } from '@/lib/summaryQuery';
import {
  DataProvider,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  GetManyParams,
  GetManyResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  UpdateParams,
  UpdateResult,
  UpdateManyParams,
  UpdateManyResult,
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  DeleteManyParams,
  DeleteManyResult,
  RaRecord,
  Identifier,
} from 'react-admin';
import queryString from 'query-string';
import { API_CONFIG } from '@/config/constants';
import { DateTimeSystem } from '@/utils/dateTimeSystem';
import keycloak from '@/lib/keycloak';
import { IMPERSONATION_HEADER, getImpersonation, stopImpersonation } from '@/utils/impersonation';
import type {
  DeviceStats,
  ErrorEvent,
  ErrorStats,
  ErrorByDevice,
  ErrorFilters,
  AbandonmentEvent,
  AbandonmentAnalysis,
  AbandonmentFilters,
  PaginatedResult,
  JourneyEvents,
} from '@/types/models';
import type { ChannelAnalytics } from '@/types/channelAnalytics';
import type { DispenserByHotelResponse, DispenserDailyTrendResponse, DispenserQuery, DispenserSummary } from '@/types/dispensers';
import type { AskResponse, AskStatus, AskTurn, AskView } from '@/types/assistant';
import type { OperatorCodeDispenser } from '@/types/models';
import { parseBenchmarkResponse } from '@/lib/benchmarkView';
import type { ManualChapter, ManualIndex, ManualLang, ManualSearchResponse } from '@/features/manual/types';
import type { ReleaseNote } from '@/features/release-notes/types';
import type {
  BenchmarkResponse,
  BoardResponse,
  MetricResultDto,
  OperacionResponse,
  StatusResponse,
  SurveyChannel,
  SurveyResponsesPage,
  SurveySummaryResponse,
  V2Property,
} from '@/types/analyticsV2';
import type {
  AnalyticsArrivalsResponse,
  DurationsResponse,
  PaymentsResponse,
  HotelsOverviewResponse,
  GroupSettings,
} from '@/types/analytics';
import type {
  ArrivalsResponse,
  ReservationsFilter,
  ReservationsResponse,
  ReservationDetailResponse,
  TimelineResponse,
  HotelSettings,
} from '@/types/reservationState';
import type {
  ErrorDefinition,
  ErrorDefinitionLookup,
  ErrorDefinitionBatchResponse,
  ErrorDefinitionCreateRequest,
  ErrorDefinitionUpdateRequest,
  ErrorDefinitionListResponse,
} from '@/types/errorDefinitions';
import { buildArrivalsQuery, type ArrivalsQueryParams } from '@/lib/arrivalsQuery';
import { reservationsQueryString } from '@/features/reservations/lib/reservationsQuery';

const apiUrl = API_CONFIG.BASE_URL;
// v2 read API (w7.u1): same host, sibling path — `BASE_URL` already ends in
// `/api/v1/ui`, so this derives `/api/v2/ui` from it rather than adding a
// second env var for one path segment.
const apiUrlV2 = apiUrl.replace(/\/api\/v1\/ui$/, '/api/v2/ui');

// ── Simple in-memory cache for static-ish resources ──────────────
const CACHEABLE_RESOURCES = new Set(['hotels', 'groups', 'devices']);
const CACHE_TTL_MS = 30_000; // 30 seconds

interface CacheEntry {
  data: any;
  timestamp: number;
}

const resourceCache = new Map<string, CacheEntry>();

const getCacheKey = (resource: string, params: any): string => {
  // For cacheable resources, ignore sort/pagination — they always fetch all records.
  // Only differentiate by filter to avoid serving wrong filtered data.
  const filter = params?.filter;
  const filterKey = filter && Object.keys(filter).length > 0 ? JSON.stringify(filter) : '';
  return `${resource}::${filterKey}`;
};

const getCached = (key: string): any | null => {
  const entry = resourceCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    resourceCache.delete(key);
    return null;
  }
  return entry.data;
};

const setCache = (key: string, data: any): void => {
  resourceCache.set(key, { data, timestamp: Date.now() });
};

const invalidateResource = (resource: string): void => {
  for (const key of resourceCache.keys()) {
    if (key.startsWith(`${resource}::`)) {
      resourceCache.delete(key);
    }
  }
};

// Fetch options interface
interface FetchOptions extends RequestInit {
  user?: {
    authenticated?: boolean;
  };
}

/**
 * Authenticated fetch: fresh token, impersonation header, JSON content type
 * for a body, and one retry after renewing the token on a 401. Returns the
 * raw response, so a caller can read something other than JSON (a manual
 * image is a blob).
 */
export const authorizedFetch = async (url: string, options: FetchOptions = {}): Promise<Response> => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  
  const headers = options.headers as Headers;

  // Ensure token is fresh before each request
  if (keycloak.isTokenExpired(30)) {
    try {
      await keycloak.updateToken(30);
    } catch {
      // Token refresh failed — will result in 401 handled by authProvider
    }
  }
  if (keycloak.token) {
    headers.set('Authorization', `Bearer ${keycloak.token}`);
  }

  // Downward impersonation: the admin keeps their own token; the API swaps the
  // request's principal for this subject after validating the real role.
  const impersonation = getImpersonation();
  if (impersonation) {
    headers.set(IMPERSONATION_HEADER, impersonation.sub);
  }

  // Add Content-Type for requests with body (except FormData)
  if (options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  let response = await fetch(url, options);

  // The token can lapse between the freshness check above and the server's own
  // check — a request sent in the last seconds of its life, or while a refresh
  // was still in flight. The server refuses it before handling it, and the
  // screen showed «No se ha podido cargar la información» for a session that
  // was still valid (Instalaciones, first load, ten minutes after signing in).
  // Renew the token once and repeat the request; a second refusal is real.
  if (response.status === 401 && !options.signal?.aborted) {
    const renewed = await keycloak.updateToken(-1).then(() => true, () => false);
    if (renewed && keycloak.token) {
      headers.set('Authorization', `Bearer ${keycloak.token}`);
      response = await fetch(url, options);
    }
  }

  return response;
};

/**
 * HTTP client with authentication
 */
const httpClient = async (url: string, options: FetchOptions = {}): Promise<any> => {
  const response = await authorizedFetch(url, options);
  const impersonation = getImpersonation();

  const text = await response.text();
  const json = text ? JSON.parse(text) : {};

  if (response.status < 200 || response.status >= 300) {
    const errorMessage =
      json.message ||
      (typeof json.error === 'string' ? json.error : json.error?.message) ||
      response.statusText;
    const error: any = new Error(errorMessage);
    error.status = response.status;
    error.body = json;

    // Failsafe: if the API rejects the impersonation itself (target revoked,
    // disabled, role raised…), drop the state and come back as the real user
    // instead of leaving every request failing.
    if (impersonation && response.status === 403 && String(json?.error?.code ?? '').startsWith('IMPERSONATION')) {
      stopImpersonation();
      window.location.hash = '#/';
      window.location.reload();
    }

    throw error;
  }

  return { json, headers: response.headers };
};

/**
 * Map resource names to API endpoints
 */
const getResourceEndpoint = (resource: string): string => {
  const endpointMap: Record<string, string> = {
    'users': 'users',
    'groups': 'groups',
    'hotels': 'hotels',
    'check-ins': 'check-ins',
    'journeys': 'journeys',
    'devices': 'devices',
    'alerts': 'alerts',
    'events': 'events',
    'operations': 'operations',
  };

  return endpointMap[resource] || resource;
};

/**
 * Base data provider with React Admin methods
 */
const baseDataProvider: DataProvider = {
  /**
   * GET_LIST - Get list of records
   */
  getList: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetListParams
  ): Promise<GetListResult<RecordType>> => {
    // Check cache for static-ish resources
    if (CACHEABLE_RESOURCES.has(resource)) {
      const cacheKey = getCacheKey(resource, params);
      const cached = getCached(cacheKey);
      if (cached) return cached as GetListResult<RecordType>;
    }

    const { pagination, sort, filter } = params;
    const { page, perPage } = pagination;
    const { field, order } = sort;

    // Extract client-side-only filters (not sent to backend)
    const { excludeRoles, ...backendFilter } = filter;

    // Extract array-based 'id' filter for client-side filtering
    // (backend doesn't support filtering by id array)
    let clientIdFilter: string[] | undefined;
    if (Array.isArray(backendFilter.id)) {
      clientIdFilter = backendFilter.id;
      delete backendFilter.id;
    }

    // Build query params
    const query: any = {
      page: page,
      pageSize: perPage,
      sortBy: field,
      sortOrder: order.toLowerCase(),
      ...backendFilter,
    };

    // Map 'q' filter to 'search' for backend
    if (backendFilter.q) {
      query.search = backendFilter.q;
      delete query.q;
    }

    const endpoint = getResourceEndpoint(resource);
    const url = `${apiUrl}/${endpoint}?${queryString.stringify(query)}`;

    const { json } = await httpClient(url);

    // Handle different response formats
    let data: RecordType[] = [];
    let total = 0;

    // Format 1: { data: [...], pagination: { totalItems | totalCount | total: N } }
    if (json.data && Array.isArray(json.data)) {
      data = json.data;
      if (json.pagination) {
        total = json.pagination.totalItems || json.pagination.totalCount || json.pagination.total || data.length;
      } else {
        total = json.totalItems || json.totalCount || json.total || data.length;
      }
    }
    // Format 2: { items: [...], totalItems | totalCount: N }
    else if (json.items && Array.isArray(json.items)) {
      data = json.items;
      total = json.totalItems || json.totalCount || json.total || data.length;
    }
    // Format 3: Direct array
    else if (Array.isArray(json)) {
      data = json;
      total = json.length;
    }
    // Format 4: Response with resource name
    else if (json[resource] && Array.isArray(json[resource])) {
      data = json[resource];
      total = json.totalCount || json.total || data.length;
    }

    // Convert dates from backend format
    let convertedData = DateTimeSystem.convertFromBackend(data);

    // Client-side filtering by id array (e.g. assigned groups for multi-group GroupAdmin)
    if (clientIdFilter && clientIdFilter.length > 0) {
      const idSet = new Set(clientIdFilter);
      convertedData = convertedData.filter((item: any) => idSet.has(item.id));
      total = convertedData.length;
    }

    // Client-side filtering for users by role (security layer)
    // This ensures users don't see roles they shouldn't, even if backend doesn't filter
    if (resource === 'users' && excludeRoles && Array.isArray(excludeRoles) && excludeRoles.length > 0) {
      const originalCount = convertedData.length;
      convertedData = convertedData.filter((user: any) => !excludeRoles.includes(user.role));
      // Adjust total count based on filtered results
      const filteredCount = originalCount - convertedData.length;
      total = Math.max(0, total - filteredCount);
    }

    const result = {
      data: convertedData as RecordType[],
      total: total,
    };

    // Cache result for static-ish resources
    if (CACHEABLE_RESOURCES.has(resource)) {
      setCache(getCacheKey(resource, params), result);
    }

    return result;
  },

  /**
   * GET_ONE - Get a single record
   */
  getOne: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetOneParams
  ): Promise<GetOneResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    const url = `${apiUrl}/${endpoint}/${params.id}`;
    
    const { json } = await httpClient(url);
    
    // Handle different response formats
    const data = json.data || json;
    const convertedData = DateTimeSystem.convertFromBackend(data);
    
    return {
      data: convertedData as RecordType,
    };
  },

  /**
   * GET_MANY - Get multiple records by IDs
   */
  getMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyParams
  ): Promise<GetManyResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    const query = {
      ids: params.ids.join(','),
    };
    
    const url = `${apiUrl}/${endpoint}?${queryString.stringify(query)}`;
    const { json } = await httpClient(url);
    
    const data = json.data || json.items || json;
    const convertedData = DateTimeSystem.convertFromBackend(data);
    
    return {
      data: convertedData as RecordType[],
    };
  },

  /**
   * GET_MANY_REFERENCE - Get records by reference
   */
  getManyReference: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyReferenceParams
  ): Promise<GetManyReferenceResult<RecordType>> => {
    const { pagination, sort, filter, target, id } = params;
    const { page, perPage } = pagination;
    const { field, order } = sort;
    
    const query = {
      page: page,
      pageSize: perPage,
      sortBy: field,
      sortOrder: order.toLowerCase(),
      ...filter,
      [target]: id,
    };
    
    const endpoint = getResourceEndpoint(resource);
    const url = `${apiUrl}/${endpoint}?${queryString.stringify(query)}`;
    
    const { json } = await httpClient(url);
    
    let data: RecordType[] = [];
    let total = 0;
    
    if (json.data && Array.isArray(json.data)) {
      data = json.data;
      total = json.totalCount || json.total || data.length;
    } else if (Array.isArray(json)) {
      data = json;
      total = json.length;
    }
    
    const convertedData = DateTimeSystem.convertFromBackend(data);
    
    return {
      data: convertedData as RecordType[],
      total: total,
    };
  },

  /**
   * CREATE - Create a new record
   */
  create: async <RecordType extends RaRecord = any>(
    resource: string,
    params: CreateParams
  ): Promise<CreateResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    const url = `${apiUrl}/${endpoint}`;
    
    const body = DateTimeSystem.convertForBackend(params.data);

    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    invalidateResource(resource);

    const data = json.data || json;
    const convertedData = DateTimeSystem.convertFromBackend(data);

    return {
      data: convertedData as RecordType,
    };
  },

  /**
   * UPDATE - Update a record
   */
  update: async <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateParams
  ): Promise<UpdateResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    const url = `${apiUrl}/${endpoint}/${params.id}`;
    
    let body = DateTimeSystem.convertForBackend(params.data);
    
    // For devices, send ONLY editable fields (exclude read-only fields)
  if (resource === 'devices') {
    const { hotelId, groupId, status, channel, serialNumber, name } = body;
    body = {
      hotelId,
      groupId,
      status,
      channel,
      serialNumber,
      name,
    };
  }

    const { json } = await httpClient(url, {
      method: 'PUT',
      body: JSON.stringify(body),
    });

    invalidateResource(resource);

    const data = json.data || json;

    const convertedData = DateTimeSystem.convertFromBackend(data);

    return {
      data: convertedData as RecordType,
    };
  },

  /**
   * UPDATE_MANY - Update multiple records
   */
  updateMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateManyParams
  ): Promise<UpdateManyResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    const body = DateTimeSystem.convertForBackend(params.data);
    
    try {
      // Try bulk update if API supports it
      const url = `${apiUrl}/${endpoint}/bulk-update`;

      await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify({
          ids: params.ids,
          data: body,
        }),
      });

      invalidateResource(resource);

      return {
        data: params.ids,
      };
    } catch (error) {
      // If bulk update not supported, do multiple updates
      const promises = params.ids.map((id: Identifier) =>
        httpClient(`${apiUrl}/${endpoint}/${id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })
          .then(() => id)
          .catch(() => null as Identifier | null)
      );

      const results = await Promise.all(promises);
      const successfulIds = results.filter((id): id is Identifier => id !== null);

      invalidateResource(resource);

      return {
        data: successfulIds,
      };
    }
  },

  /**
   * DELETE - Delete a record
   */
  delete: async <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteParams
  ): Promise<DeleteResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    const url = `${apiUrl}/${endpoint}/${params.id}`;
    
    await httpClient(url, {
      method: 'DELETE',
    });

    invalidateResource(resource);

    return {
      data: params.previousData as RecordType,
    };
  },

  /**
   * DELETE_MANY - Delete multiple records
   */
  deleteMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteManyParams
  ): Promise<DeleteManyResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    
    try {
      // Try bulk delete if API supports it
      const url = `${apiUrl}/${endpoint}/bulk-delete`;

      await httpClient(url, {
        method: 'DELETE',
        body: JSON.stringify({
          ids: params.ids,
        }),
      });

      invalidateResource(resource);

      return {
        data: params.ids,
      };
    } catch (error) {
      // If bulk delete not supported, do multiple deletes
      const promises = params.ids.map((id: Identifier) =>
        httpClient(`${apiUrl}/${endpoint}/${id}`, {
          method: 'DELETE',
        })
          .then(() => id)
          .catch(() => null as Identifier | null)
      );

      const results = await Promise.all(promises);
      const successfulIds = results.filter((id): id is Identifier => id !== null);

      invalidateResource(resource);

      return {
        data: successfulIds,
      };
    }
  },
};

/**
 * Extended data provider with Analytics-specific methods
 */
const extendedDataProvider = {
  ...baseDataProvider,

  // ============================================
  // ANALYTICS CUSTOM METHODS
  // ============================================

  /**
   * Get per-channel journey analytics: channel mix, transitions and the
   * milestone funnel broken down by channel. Wave 3: `hotelIds` (Decision 6)
   * and `axis` (arrival/activity, Decision-14 default rule lives server-side).
   */
  getChannelAnalytics: async (
  hotelId?: string,
  groupId?: string,
  dateFrom?: string,
  dateTo?: string,
  hotelIds?: string,
  axis?: 'arrival' | 'activity'
): Promise<{ data: ChannelAnalytics }> => {
  const query: any = {};

  if (hotelId) query.hotelId = hotelId;
  if (groupId) query.groupId = groupId;
  if (dateFrom) query.dateFrom = dateFrom;
  if (dateTo) query.dateTo = dateTo;
  if (hotelIds) query.hotelIds = hotelIds;
  if (axis) query.axis = axis;

  const url = `${apiUrl}/analytics/channels?${queryString.stringify(query)}`;

  try {
    const { json } = await httpClient(url);
    const data = json.data || json;
    return { data: DateTimeSystem.convertFromBackend(data) };
  } catch (error) {
    console.error('Error fetching channel analytics:', error);
    throw error;
  }
},

  /**
   * Get the reconstructed event log for a journey: events correlated by
   * reservation number / alternates / metadata.journeyId within a time
   * window, plus the per-channel attempt breakdown used to group them.
   */
  getJourneyEvents: async (
  journeyId: string,
  windowHours = 72
): Promise<{ data: JourneyEvents }> => {
  const url = `${apiUrl}/journeys/${journeyId}/events?${queryString.stringify({ window: windowHours })}`;

  try {
    const { json } = await httpClient(url);
    const data = json.data || json;
    return { data: DateTimeSystem.convertFromBackend(data) };
  } catch (error) {
    console.error('Error fetching journey events:', error);
    throw error;
  }
},


  /**
   * Get device status statistics
   */
  getDeviceStats: async (
  hotelId?: string,
  groupId?: string
): Promise<{ data: DeviceStats }> => {
  const query: any = {};
  
  if (hotelId) query.hotelId = hotelId;
  if (groupId) query.groupId = groupId;
  
  const url = `${apiUrl}/analytics/device-stats?${queryString.stringify(query)}`;
  
  try {
    const { json } = await httpClient(url);
    const data = json.data || json;
    return { data: DateTimeSystem.convertFromBackend(data) };
  } catch (error) {
    console.error('Error fetching device stats:', error);
    throw error;
  }
},

  /**
   * Register a new device (auto-discovery)
   */
  registerDevice: async (deviceData: {
    deviceId: string;
    installationCode: string;
    type: string;
  }): Promise<{ data: any }> => {
    const url = `${apiUrl}/devices/register`;
    
    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(deviceData),
      });
      
      const data = json.data || json;
      return { data: DateTimeSystem.convertFromBackend(data) };
    } catch (error) {
      console.error('Error registering device:', error);
      throw error;
    }
  },

  /**
   * ALERTS MANAGEMENT METHODS
   */
  
  /**
   * Update alert status (acknowledge or resolve)
   */
  updateAlertStatus: async (
    alertId: string,
    status: 'Acknowledged' | 'Resolved'
  ): Promise<{ data: any }> => {
    const url = `${apiUrl}/alerts/${alertId}/status`;
    
    try {
      const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      
      const data = json.data || json;
      return { data: DateTimeSystem.convertFromBackend(data) };
    } catch (error) {
      console.error('Error updating alert status:', error);
      throw error;
    }
  },

  /**
 * Get pending alerts (not yet sent)
 */
getPendingAlerts: async (): Promise<{ data: any[] }> => {
  const url = `${apiUrl}/alerts/pending`;
  
  try {
    const { json } = await httpClient(url);
    const data = json.data || json;
    return { data: DateTimeSystem.convertFromBackend(data) };
  } catch (error) {
    console.error('Error fetching pending alerts:', error);
    throw error;
  }
},

/**
 * Send specific alert manually
 */
sendAlert: async (alertId: string): Promise<{ data: any }> => {
  const url = `${apiUrl}/alerts/${alertId}/send`;
  
  try {
    const { json } = await httpClient(url, {
      method: 'POST',
    });
    
    return { data: json.data || json };
  } catch (error) {
    console.error('Error sending alert:', error);
    throw error;
  }
},

/**
 * Send all pending alerts
 */
sendPendingAlerts: async (): Promise<{ data: any }> => {
  const url = `${apiUrl}/alerts/send-pending`;
  
  try {
    const { json } = await httpClient(url, {
      method: 'POST',
    });
    
    return { data: json.data || json };
  } catch (error) {
    console.error('Error sending pending alerts:', error);
    throw error;
  }
},

/**
 * Get alert configurations
 */
getAlertConfigurations: async (): Promise<{ data: any[] }> => {
  const url = `${apiUrl}/alerts/configurations`;
  
  try {
    const { json } = await httpClient(url);
    const data = json.data || json;
    return { data: DateTimeSystem.convertFromBackend(data) };
  } catch (error) {
    console.error('Error fetching alert configurations:', error);
    throw error;
  }
},

/**
 * Update alert configuration
 */
updateAlertConfiguration: async (
  configId: string,
  updates: any
): Promise<{ data: any }> => {
  const url = `${apiUrl}/alerts/configurations/${configId}`;
  
  try {
    const { json } = await httpClient(url, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    
    const data = json.data || json;
    return { data: DateTimeSystem.convertFromBackend(data) };
  } catch (error) {
    console.error('Error updating alert configuration:', error);
    throw error;
  }
},








  /**
   * Export check-ins to Excel
   */
  exportCheckIns: async (
    hotelId?: string,
    groupId?: string,
    dateFrom?: string,
    dateTo?: string,
    channel?: string
  ): Promise<Blob> => {
    const query: any = {};
    
    if (hotelId) query.hotelId = hotelId;
    if (groupId) query.groupId = groupId;
    if (dateFrom) query.dateFrom = dateFrom;
    if (dateTo) query.dateTo = dateTo;
    if (channel) query.channel = channel;
    
    const url = `${apiUrl}/check-ins/export?${queryString.stringify(query)}`;

    if (keycloak.isTokenExpired(30)) {
      await keycloak.updateToken(30);
    }
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    return await response.blob();
  },

  /**
   * Export journeys to Excel
   */
  exportJourneys: async (
    hotelId?: string,
    groupId?: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<Blob> => {
    const query: any = {};
    
    if (hotelId) query.hotelId = hotelId;
    if (groupId) query.groupId = groupId;
    if (dateFrom) query.dateFrom = dateFrom;
    if (dateTo) query.dateTo = dateTo;
    
    const url = `${apiUrl}/journeys/export?${queryString.stringify(query)}`;

    if (keycloak.isTokenExpired(30)) {
      await keycloak.updateToken(30);
    }
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    return await response.blob();
  },

  /**
   * Get current user info
   */
  getCurrentUser: async () => {
    const url = `${apiUrl}/users/me`;
    const { json } = await httpClient(url);
    return { data: DateTimeSystem.convertFromBackend(json) };
  },

  /**
   * Get paginated error events
   */
  getErrorEvents: async (
    filters: ErrorFilters,
    page: number = 1,
    pageSize: number = 50
  ): Promise<{ data: PaginatedResult<ErrorEvent> }> => {
    const query: any = {
      page,
      pageSize,
    };

    if (filters.hotelId) query.hotelId = filters.hotelId;
    if (filters.hotelIds) query.hotelIds = filters.hotelIds;
    if (filters.groupId) query.groupId = filters.groupId;
    if (filters.deviceId) query.deviceId = filters.deviceId;
    if (filters.sectionCode) query.sectionCode = filters.sectionCode;
    if (filters.dateFrom) query.dateFrom = filters.dateFrom;
    if (filters.dateTo) query.dateTo = filters.dateTo;

    const url = `${apiUrl}/events/errors?${queryString.stringify(query)}`;

    try {
      const { json } = await httpClient(url);
      
      return {
        data: {
          items: json.data || [],
          totalItems: json.pagination?.totalItems || 0,
          page: json.pagination?.page || page,
          pageSize: json.pagination?.pageSize || pageSize,
          totalPages: json.pagination?.totalPages || 0,
        },
      };
    } catch (error) {
      console.error('Error fetching error events:', error);
      throw error;
    }
  },

  /**
   * Get error statistics
   */
  getErrorStats: async (
    hotelId?: string,
    groupId?: string,
    dateFrom?: string,
    dateTo?: string,
    hotelIds?: string
  ): Promise<{ data: ErrorStats }> => {
    const query: any = {};

    if (hotelId) query.hotelId = hotelId;
    if (groupId) query.groupId = groupId;
    if (dateFrom) query.dateFrom = dateFrom;
    if (dateTo) query.dateTo = dateTo;
    if (hotelIds) query.hotelIds = hotelIds;

    const url = `${apiUrl}/events/stats/errors?${queryString.stringify(query)}`;

    try {
      const { json } = await httpClient(url);
      return { data: json.data };
    } catch (error) {
      console.error('Error fetching error stats:', error);
      throw error;
    }
  },

  /**
   * Get paginated abandonment events
   */
  getAbandonmentEvents: async (
    filters: AbandonmentFilters,
    page: number = 1,
    pageSize: number = 50
  ): Promise<{ data: PaginatedResult<AbandonmentEvent> }> => {
    const query: any = {
      page,
      pageSize,
    };

    if (filters.hotelId) query.hotelId = filters.hotelId;
    if (filters.groupId) query.groupId = filters.groupId;
    if (filters.deviceId) query.deviceId = filters.deviceId;
    if (filters.sectionCode) query.sectionCode = filters.sectionCode;
    if (filters.dateFrom) query.dateFrom = filters.dateFrom;
    if (filters.dateTo) query.dateTo = filters.dateTo;

    const url = `${apiUrl}/events/abandoned?${queryString.stringify(query)}`;

    try {
      const { json } = await httpClient(url);
      
      return {
        data: {
          items: json.data || [],
          totalItems: json.pagination?.totalItems || 0,
          page: json.pagination?.page || page,
          pageSize: json.pagination?.pageSize || pageSize,
          totalPages: json.pagination?.totalPages || 0,
        },
      };
    } catch (error) {
      console.error('Error fetching abandonment events:', error);
      throw error;
    }
  },

  /**
   * Get abandonment analysis
   */
  getAbandonmentAnalysis: async (
    hotelId?: string,
    groupId?: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<{ data: AbandonmentAnalysis }> => {
    const query: any = {};

    if (hotelId) query.hotelId = hotelId;
    if (groupId) query.groupId = groupId;
    if (dateFrom) query.dateFrom = dateFrom;
    if (dateTo) query.dateTo = dateTo;

    const url = `${apiUrl}/events/analysis/abandonment?${queryString.stringify(query)}`;

    try {
      const { json } = await httpClient(url);
      return { data: json.data };
    } catch (error) {
      console.error('Error fetching abandonment analysis:', error);
      throw error;
    }
  },

  
  // ============================================
  // NOTIFIER CONFIGURATION METHODS
  // ============================================

  /**
   * Get notifier configuration for a hotel
   */
  getNotifierConfig: async (hotelId: string): Promise<{ data: any }> => {
    const url = `${apiUrl}/hotels/${hotelId}/notifier-config`;
    
    try {
      const { json } = await httpClient(url);
      const data = json.data || json;
      return { data: DateTimeSystem.convertFromBackend(data) };
    } catch (error) {
      console.error('Error fetching notifier config:', error);
      throw error;
    }
  },

  /**
   * Update notifier configuration for a hotel
   */
  updateNotifierConfig: async (
    hotelId: string,
    config: any
  ): Promise<{ data: any }> => {
    const url = `${apiUrl}/hotels/${hotelId}/notifier-config`;
    
    try {
      const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      
      const data = json.data || json;
      return { data: DateTimeSystem.convertFromBackend(data) };
    } catch (error) {
      console.error('Error updating notifier config:', error);
      throw error;
    }
  },

  /**
   * Disable all alerts for a hotel
   */
  disableNotifierAlerts: async (hotelId: string): Promise<{ data: any }> => {
    const url = `${apiUrl}/hotels/${hotelId}/notifier-config/disable`;
    
    try {
      const { json } = await httpClient(url, {
        method: 'PUT',
      });
      
      const data = json.data || json;
      return { data: DateTimeSystem.convertFromBackend(data) };
    } catch (error) {
      console.error('Error disabling alerts:', error);
      throw error;
    }
  },

  /**
 * Get available templates for a channel
 */
getNotifierTemplates: async (
  hotelId: string,
  channelType: string
): Promise<{ data: any[] }> => {
  // Construct query parameter
  const query = {
    channelType: channelType
  };
  
  const url = `${apiUrl}/hotels/${hotelId}/notifier-config/templates?${queryString.stringify(query)}`;
  
  try {
    const { json } = await httpClient(url);
    const data = json.data || json;
    return { data: Array.isArray(data) ? data : [] };
  } catch (error) {
    console.error('Error fetching templates:', error);
    // Return empty array on error instead of throwing
    return { data: [] };
  }
},

  /**
   * Get template variables
   */
  getTemplateVariables: async (
    hotelId: string,
    templateId: string
  ): Promise<{ data: any[] }> => {
    const url = `${apiUrl}/hotels/${hotelId}/notifier-config/templates/${templateId}/variables`;

    try {
      const { json } = await httpClient(url);
      const data = json.data || json;
      return { data: Array.isArray(data) ? data : [] };
    } catch (error) {
      console.error('Error fetching template variables:', error);
      // Return empty array on error instead of throwing
      return { data: [] };
    }
  },

  // ============================================
  // ERROR DEFINITIONS API METHODS
  // ============================================

  /**
   * Get all error definitions (paginated)
   */
  getErrorDefinitions: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    category?: string;
    severity?: string;
    isActive?: boolean;
  }): Promise<{ data: ErrorDefinitionListResponse }> => {
    const query: any = {
      page: params?.page || 1,
      pageSize: params?.pageSize || 50,
    };

    if (params?.search) query.search = params.search;
    if (params?.category) query.category = params.category;
    if (params?.severity) query.severity = params.severity;
    if (params?.isActive !== undefined) query.isActive = params.isActive;

    const url = `${apiUrl}/error-definitions?${queryString.stringify(query)}`;

    try {
      const { json } = await httpClient(url);
      // Transform API response to expected format
      // API returns: { items, totalItems, page, pageSize, totalPages }
      // We expect: { data, pagination }
      const transformed = {
        data: json.items || json.data || [],
        pagination: {
          page: json.page || 1,
          pageSize: json.pageSize || 25,
          totalCount: json.totalItems || json.total || 0,
          totalPages: json.totalPages || 1,
        },
      };
      return { data: DateTimeSystem.convertFromBackend(transformed) };
    } catch (error) {
      console.error('Error fetching error definitions:', error);
      throw error;
    }
  },

  /**
   * Get a single error definition by ID
   */
  getErrorDefinition: async (id: string): Promise<{ data: ErrorDefinition }> => {
    const url = `${apiUrl}/error-definitions/${id}`;

    try {
      const { json } = await httpClient(url);
      return { data: DateTimeSystem.convertFromBackend(json.data || json) };
    } catch (error) {
      console.error('Error fetching error definition:', error);
      throw error;
    }
  },

  /**
   * Lookup single error code translation
   * GET /api/v1/ui/error-definitions/{errorCode}/translation?language={lang}
   */
  lookupErrorDefinition: async (
    errorCode: string,
    language: string = 'en'
  ): Promise<{ data: ErrorDefinitionLookup | null }> => {
    const query = { language };
    const url = `${apiUrl}/error-definitions/${errorCode}/translation?${queryString.stringify(query)}`;

    try {
      const { json } = await httpClient(url);
      return { data: json.data || json };
    } catch (error: any) {
      // Return null if not found (404)
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        return { data: null };
      }
      console.error('Error looking up error definition:', error);
      throw error;
    }
  },

  /**
   * Batch lookup multiple error codes
   * Fetches translations for multiple error codes in parallel
   */
  lookupErrorDefinitionsBatch: async (
    errorCodes: string[],
    language: string = 'en'
  ): Promise<{ data: ErrorDefinitionBatchResponse }> => {
    try {
      // Fetch all translations in parallel
      const results = await Promise.allSettled(
        errorCodes.map(async (errorCode) => {
          const query = { language };
          const url = `${apiUrl}/error-definitions/${errorCode}/translation?${queryString.stringify(query)}`;
          const { json } = await httpClient(url);
          return { errorCode, data: json.data || json };
        })
      );

      const definitions: Record<string, ErrorDefinitionLookup> = {};
      const missing: string[] = [];

      results.forEach((result, index) => {
        const errorCode = errorCodes[index];
        if (result.status === 'fulfilled' && result.value.data) {
          definitions[errorCode] = result.value.data;
        } else {
          missing.push(errorCode);
        }
      });

      return { data: { definitions, missing } };
    } catch (error) {
      console.error('Error batch looking up error definitions:', error);
      return {
        data: {
          definitions: {},
          missing: errorCodes,
        },
      };
    }
  },

  /**
   * Create a new error definition (SuperAdmin only)
   */
  createErrorDefinition: async (
    data: ErrorDefinitionCreateRequest
  ): Promise<{ data: ErrorDefinition }> => {
    const url = `${apiUrl}/error-definitions`;

    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return { data: DateTimeSystem.convertFromBackend(json.data || json) };
    } catch (error) {
      console.error('Error creating error definition:', error);
      throw error;
    }
  },

  /**
   * Update an error definition (SuperAdmin only)
   * Uses PUT method as per backend API
   */
  updateErrorDefinition: async (
    id: string,
    data: ErrorDefinitionUpdateRequest
  ): Promise<{ data: ErrorDefinition }> => {
    const url = `${apiUrl}/error-definitions/${encodeURIComponent(id)}`;

    try {
      const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return { data: DateTimeSystem.convertFromBackend(json.data || json) };
    } catch (error) {
      console.error('Error updating error definition:', error);
      throw error;
    }
  },

  /**
   * Delete an error definition (SuperAdmin only)
   */
  deleteErrorDefinition: async (id: string): Promise<{ data: { success: boolean } }> => {
    const url = `${apiUrl}/error-definitions/${id}`;

    try {
      await httpClient(url, {
        method: 'DELETE',
      });
      return { data: { success: true } };
    } catch (error) {
      console.error('Error deleting error definition:', error);
      throw error;
    }
  },

  // ============================================
  // FORCE DELETE METHODS (SuperAdmin only)
  // ============================================

  /**
   * Force delete a hotel and all related data
   * DELETE /api/v1/ui/hotels/{hotelId}/force
   */
  forceDeleteHotel: async (hotelId: string): Promise<{ data: any }> => {
    const url = `${apiUrl}/hotels/${hotelId}/force`;
    console.log('[forceDeleteHotel] Attempting DELETE:', url, 'hotelId:', hotelId);

    try {
      const { json } = await httpClient(url, {
        method: 'DELETE',
      });
      console.log('[forceDeleteHotel] Success:', json);
      return { data: json.data || json };
    } catch (error) {
      console.error('[forceDeleteHotel] Error:', error);
      throw error;
    }
  },

  /**
   * Force delete a device and all related data
   * DELETE /api/v1/devices/{deviceId}/force
   */
  forceDeleteDevice: async (deviceId: string): Promise<{ data: any }> => {
    const url = `${apiUrl}/devices/${deviceId}/force`;

    try {
      const { json } = await httpClient(url, {
        method: 'DELETE',
      });
      return { data: json.data || json };
    } catch (error) {
      console.error('Error force deleting device:', error);
      throw error;
    }
  },

  // ============================================
  // DETAILED ERROR EVENTS (UI Endpoint)
  // ============================================

  /**
   * Get detailed error events with all related information
   * GET /api/v1/ui/events/errors
   */
  /**
   * Error monitor «Por dispositivo»: the same error events as
   * getDetailedErrorEvents (same filters), counted per device.
   */
  getErrorsByDevice: async (filters: ErrorFilters): Promise<{ data: ErrorByDevice[] }> => {
    const query: any = {};
    if (filters.hotelId) query.hotelId = filters.hotelId;
    if (filters.hotelIds) query.hotelIds = filters.hotelIds;
    if (filters.groupId) query.groupId = filters.groupId;
    if (filters.deviceId) query.deviceId = filters.deviceId;
    if (filters.sectionCode) query.sectionCode = filters.sectionCode;
    if (filters.dateFrom) query.dateFrom = filters.dateFrom;
    if (filters.dateTo) query.dateTo = filters.dateTo;
    if (filters.severity) query.severity = filters.severity;
    if (filters.errorCode) query.errorCode = filters.errorCode;
    if (filters.category) query.category = filters.category;
    if (filters.nature) query.nature = filters.nature;

    const url = `${apiUrl}/events/errors/by-device?${queryString.stringify(query)}`;
    const { json } = await httpClient(url);
    return { data: (json.data ?? []) as ErrorByDevice[] };
  },

  getDetailedErrorEvents: async (
    filters: ErrorFilters,
    page: number = 1,
    pageSize: number = 25,
    language: string = 'en'
  ): Promise<{ data: any }> => {
    const query: any = {
      page,
      pageSize,
      language,
    };

    if (filters.hotelId) query.hotelId = filters.hotelId;
    if (filters.hotelIds) query.hotelIds = filters.hotelIds;
    if (filters.groupId) query.groupId = filters.groupId;
    if (filters.deviceId) query.deviceId = filters.deviceId;
    if (filters.sectionCode) query.sectionCode = filters.sectionCode;
    if (filters.dateFrom) query.dateFrom = filters.dateFrom;
    if (filters.dateTo) query.dateTo = filters.dateTo;
    if (filters.severity) query.severity = filters.severity;
    if (filters.errorCode) query.errorCode = filters.errorCode;
    if (filters.category) query.category = filters.category;
    if (filters.nature) query.nature = filters.nature;

    const url = `${apiUrl}/events/errors?${queryString.stringify(query)}`;

    try {
      const { json } = await httpClient(url);
      return {
        data: {
          items: json.data || [],
          totalItems: json.pagination?.totalItems || 0,
          page: json.pagination?.page || page,
          pageSize: json.pagination?.pageSize || pageSize,
          totalPages: json.pagination?.totalPages || 0,
        },
      };
    } catch (error) {
      console.error('Error fetching detailed error events:', error);
      throw error;
    }
  },

  // ============================================
  // METADATA SEARCH METHODS
  // ============================================

  /**
   * Search events by metadata key/value
   * GET /api/v1/ui/events/search/metadata
   */
  searchEventsByMetadata: async (params: {
    key: string;
    value: string;
    hotelId?: string;
    groupId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: any }> => {
    const query: any = {
      key: params.key,
      value: params.value,
      page: params.page || 1,
      pageSize: params.pageSize || 50,
    };

    if (params.hotelId) query.hotelId = params.hotelId;
    if (params.groupId) query.groupId = params.groupId;
    if (params.dateFrom) query.dateFrom = params.dateFrom;
    if (params.dateTo) query.dateTo = params.dateTo;

    const url = `${apiUrl}/events/search/metadata?${queryString.stringify(query)}`;

    try {
      const { json } = await httpClient(url);
      return {
        data: {
          items: json.data || [],
          totalItems: json.pagination?.totalItems || 0,
          page: json.pagination?.page || params.page || 1,
          pageSize: json.pagination?.pageSize || params.pageSize || 50,
          totalPages: json.pagination?.totalPages || 0,
          query: json.query,
        },
      };
    } catch (error) {
      console.error('Error searching events by metadata:', error);
      throw error;
    }
  },

  /**
   * Search devices by metadata key/value
   * GET /api/v1/ui/devices/search/metadata
   */
  searchDevicesByMetadata: async (params: {
    key: string;
    value: string;
  }): Promise<{ data: any[] }> => {
    const query = {
      key: params.key,
      value: params.value,
    };

    const url = `${apiUrl}/devices/search/metadata?${queryString.stringify(query)}`;

    try {
      const { json } = await httpClient(url);
      return {
        data: json.data || json || [],
      };
    } catch (error) {
      console.error('Error searching devices by metadata:', error);
      throw error;
    }
  },

  /** Key dispensers whose operator code the user may generate (P06), for Llegadas. */
  getOperatorCodeDispensers: async (hotelIds?: string[]): Promise<{ data: OperatorCodeDispenser[] }> => {
    const query = hotelIds && hotelIds.length > 0 ? `?hotelIds=${encodeURIComponent(hotelIds.join(','))}` : '';
    const { json } = await httpClient(`${apiUrl}/devices/operator-code/dispensers${query}`);
    return { data: json.data ?? [] };
  },

  generateDeviceOtp: async (deviceId: string, date?: string): Promise<{ data: any }> => {
    const query = date ? `?date=${date}` : '';
    const url = `${apiUrl}/devices/${deviceId}/otp${query}`;

    try {
      const { json } = await httpClient(url);
      return { data: json.data || json };
    } catch (error) {
      console.error('Error generating device OTP:', error);
      throw error;
    }
  },

  getDeviceLogs: async (deviceId: string, params?: {
    count?: number;
    level?: string;
    fromDate?: string;
    toDate?: string;
  }): Promise<{ data: any }> => {
    const query = queryString.stringify({
      count: params?.count ?? 50,
      ...(params?.level && { level: params.level }),
      ...(params?.fromDate && { fromDate: params.fromDate }),
      ...(params?.toDate && { toDate: params.toDate }),
    });
    const url = `${apiUrl}/devices/${deviceId}/logs?${query}`;

    try {
      const { json } = await httpClient(url);
      return { data: json.data || json };
    } catch (error) {
      console.error('Error fetching device logs:', error);
      throw error;
    }
  },

  queryLogs: async (params: {
    category: string;
    count?: number;
    level?: string;
    fromDate?: string;
    toDate?: string;
    insightId?: string;
    deviceId?: string;
    eventCode?: string;
    flowName?: string;
    excludeHeartbeats?: boolean;
    operation?: string;
    errorCode?: string;
    searchText?: string;
    customFilter?: string;
  }): Promise<{ data: any[]; total: number }> => {
    const query = queryString.stringify({
      category: params.category,
      count: params.count ?? 50,
      ...(params.level && { level: params.level }),
      ...(params.fromDate && { fromDate: params.fromDate }),
      ...(params.toDate && { toDate: params.toDate }),
      ...(params.insightId && { insightId: params.insightId }),
      ...(params.deviceId && { deviceId: params.deviceId }),
      ...(params.eventCode && { eventCode: params.eventCode }),
      ...(params.flowName && { flowName: params.flowName }),
      ...(params.excludeHeartbeats && { excludeHeartbeats: true }),
      ...(params.operation && { operation: params.operation }),
      ...(params.errorCode && { errorCode: params.errorCode }),
      ...(params.searchText && { searchText: params.searchText }),
      ...(params.customFilter && { customFilter: params.customFilter }),
    });
    const url = `${apiUrl}/logs?${query}`;

    try {
      const { json } = await httpClient(url);
      return { data: json.data || [], total: json.total || 0 };
    } catch (error) {
      console.error('Error querying logs:', error);
      throw error;
    }
  },

  // ============================================
  // SYSTEM ADMIN PANEL METHODS
  // ============================================

  getSystemInfo: async (): Promise<{ data: any }> => {
    const url = `${apiUrl}/system/info`;

    try {
      const { json } = await httpClient(url);
      return { data: json.data || json };
    } catch (error) {
      console.error('Error fetching system info:', error);
      throw error;
    }
  },

  getSystemJobs: async (): Promise<{ data: any[] }> => {
    const url = `${apiUrl}/system/jobs`;

    try {
      const { json } = await httpClient(url);
      return { data: json.data || json };
    } catch (error) {
      console.error('Error fetching system jobs:', error);
      throw error;
    }
  },

  triggerSystemJob: async (jobId: string): Promise<{ data: any }> => {
    const url = `${apiUrl}/system/jobs/${encodeURIComponent(jobId)}/trigger`;

    try {
      const { json } = await httpClient(url, {
        method: 'POST',
      });
      return { data: json.data || json };
    } catch (error) {
      console.error('Error triggering system job:', error);
      throw error;
    }
  },

  // ============================================
  // BULK DELETE BY FILTERS (SuperAdmin only)
  // ============================================

  deleteEventsByFilter: async (filters: Record<string, string | undefined>): Promise<{ data: { deletedCount: number } }> => {
    const url = `${apiUrl}/operations/delete-by-filter`;
    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(filters),
      });
      return { data: json.data || json };
    } catch (error) {
      console.error('Error deleting events by filter:', error);
      throw error;
    }
  },

  deleteJourneysByFilter: async (filters: Record<string, string | undefined>): Promise<{ data: { deletedCount: number } }> => {
    const url = `${apiUrl}/journeys/delete-by-filter`;
    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(filters),
      });
      return { data: json.data || json };
    } catch (error) {
      console.error('Error deleting journeys by filter:', error);
      throw error;
    }
  },

  deleteErrorEventsByFilter: async (filters: Record<string, string | undefined>): Promise<{ data: { deletedCount: number } }> => {
    const url = `${apiUrl}/error-events/delete-by-filter`;
    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(filters),
      });
      return { data: json.data || json };
    } catch (error) {
      console.error('Error deleting error events by filter:', error);
      throw error;
    }
  },

  // ============================================
  // EVENT NOTIFICATION CONFIG
  // ============================================

  getEventNotificationConfig: async (hotelId: string): Promise<{ data: any }> => {
    const url = `${apiUrl}/hotels/${hotelId}/event-notifications`;
    try {
      const { json } = await httpClient(url);
      return { data: json.data ?? null };
    } catch (error) {
      console.error('Error fetching event notification config:', error);
      throw error;
    }
  },

  updateEventNotificationConfig: async (hotelId: string, config: any): Promise<{ data: any }> => {
    const url = `${apiUrl}/hotels/${hotelId}/event-notifications`;
    try {
      const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      return { data: json.data || json };
    } catch (error) {
      console.error('Error updating event notification config:', error);
      throw error;
    }
  },

  disableEventNotifications: async (hotelId: string): Promise<{ data: any }> => {
    const url = `${apiUrl}/hotels/${hotelId}/event-notifications`;
    try {
      const { json } = await httpClient(url, { method: 'DELETE' });
      return { data: json };
    } catch (error) {
      console.error('Error disabling event notifications:', error);
      throw error;
    }
  },

  sendTestEventNotification: async (hotelId: string): Promise<{ data: any }> => {
    const url = `${apiUrl}/hotels/${hotelId}/event-notifications/test`;
    try {
      const { json } = await httpClient(url, { method: 'POST' });
      return { data: json };
    } catch (error) {
      console.error('Error sending test event notification:', error);
      throw error;
    }
  },

  applyEventNotificationConfigToGroup: async (groupId: string, sourceHotelId: string): Promise<{ data: any }> => {
    const url = `${apiUrl}/hotels/groups/${groupId}/event-notifications/apply-all`;
    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify({ sourceHotelId }),
      });
      return { data: json };
    } catch (error) {
      console.error('Error applying event notification config to group:', error);
      throw error;
    }
  },

  // ============================================
  // ACTIVITY MONITOR (live device status)
  // ============================================

  /**
   * Device status with grouped sessions for the live Activity Monitor.
   * GET /api/v1/ui/device-status/with-sessions
   *
   * Routed through httpClient so the Keycloak token is refreshed before each
   * poll (the previous hand-rolled hook read a stale token from localStorage).
   * An optional AbortSignal lets the caller cancel an in-flight poll when the
   * filter changes or the component unmounts. Errors (including AbortError) are
   * propagated unwrapped so the caller can distinguish a cancellation from a
   * real failure; logging is left to the caller to avoid noise on every abort.
   */
  getDeviceStatusWithSessions: async (
    filters: {
      hotelId?: string;
      groupId?: string;
      deviceType?: string;
      recentHours?: number;
      language?: string;
    },
    signal?: AbortSignal
  ): Promise<{ data: any[] }> => {
    const query: Record<string, any> = {};
    if (filters.hotelId) query.hotelId = filters.hotelId;
    if (filters.groupId) query.groupId = filters.groupId;
    if (filters.deviceType) query.deviceType = filters.deviceType;
    if (filters.recentHours) query.recentHours = filters.recentHours;
    if (filters.language) query.language = filters.language;

    const url = `${apiUrl}/device-status/with-sessions?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    const data = json.data ?? [];
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  /**
   * Aggregated group activity summary (helicopter view).
   * GET /api/v1/ui/device-status/group-summary
   *
   * Same rationale as getDeviceStatusWithSessions: goes through httpClient for a
   * fresh token and supports cancellation via an optional AbortSignal.
   */
  getGroupSummary: async (
    groupId: string,
    options?: { includeOffline?: boolean },
    signal?: AbortSignal
  ): Promise<{ data: any }> => {
    const query: Record<string, any> = {
      groupId,
      includeOffline: (options?.includeOffline ?? true).toString(),
    };
    const url = `${apiUrl}/device-status/group-summary?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    const data = json.data ?? json;
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  /**
   * Notification audit trail — who was notified for an event/alert, or why nobody was.
   * GET /api/v1/ui/notifications?eventId=|alertId=|deviceId=|hotelId=
   * Provide exactly one primary filter. Results are hotel-scoped server-side.
   */
  getNotifications: async (
    filters: {
      eventId?: string;
      alertId?: string;
      deviceId?: string;
      hotelId?: string;
      dateFrom?: string;
      dateTo?: string;
    },
    signal?: AbortSignal
  ): Promise<{ data: any[] }> => {
    const query: Record<string, any> = {};
    if (filters.eventId) query.eventId = filters.eventId;
    if (filters.alertId) query.alertId = filters.alertId;
    if (filters.deviceId) query.deviceId = filters.deviceId;
    if (filters.hotelId) query.hotelId = filters.hotelId;
    if (filters.dateFrom) query.dateFrom = filters.dateFrom;
    if (filters.dateTo) query.dateTo = filters.dateTo;

    const url = `${apiUrl}/notifications?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    const data = json.data ?? [];
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  // ============================================
  // SHAREABLE MONITOR PANELS (admin)
  // ============================================

  /**
   * Create a shareable panel link. Returns the raw token ONCE plus the link.
   * POST /api/v1/ui/panel-links
   */
  createPanelLink: async (input: {
    view: 'activity' | 'devices';
    scopeType: 'hotel' | 'group';
    scopeId: string;
    label?: string;
    durationHours?: number | null;
    recentHours?: number;
    layout?: 'grid' | 'columns';
    labelScopeKeys?: string | null;
  }): Promise<{ token: string; link: any }> => {
    const { json } = await httpClient(`${apiUrl}/panel-links`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = json.data ?? json;
    return { token: data.token, link: DateTimeSystem.convertFromBackend(data.link) };
  },

  /**
   * List the panel links the caller may see (non-revoked, within their scope).
   * GET /api/v1/ui/panel-links
   */
  listPanelLinks: async (): Promise<{ data: any[] }> => {
    const { json } = await httpClient(`${apiUrl}/panel-links`);
    const data = json.data ?? [];
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  /**
   * Revoke a panel link (takes effect immediately).
   * DELETE /api/v1/ui/panel-links/{id}
   */
  revokePanelLink: async (id: string): Promise<void> => {
    await httpClient(`${apiUrl}/panel-links/${id}`, { method: 'DELETE' });
  },

  // ============================================
  // LABEL CATALOG + ASSIGNMENTS
  // ============================================

  /** GET /api/v1/ui/labels/scopes — curated categories visible to the caller. */
  getLabelScopes: async (): Promise<{ data: any[] }> => {
    const { json } = await httpClient(`${apiUrl}/labels/scopes`);
    return { data: json.data ?? [] };
  },

  createLabelScope: async (input: {
    key: string;
    displayName?: string;
    color?: string;
    exclusive: boolean;
    ownerType: 'global' | 'group';
    ownerGroupId?: string;
  }): Promise<{ data: any }> => {
    const { json } = await httpClient(`${apiUrl}/labels/scopes`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return { data: json.data ?? json };
  },

  updateLabelScope: async (
    id: string,
    input: { displayName?: string; color?: string; exclusive: boolean }
  ): Promise<{ data: any }> => {
    const { json } = await httpClient(`${apiUrl}/labels/scopes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return { data: json.data ?? json };
  },

  deleteLabelScope: async (id: string): Promise<void> => {
    await httpClient(`${apiUrl}/labels/scopes/${id}`, { method: 'DELETE' });
  },

  /** GET /api/v1/ui/labels/labels — catalog values (optionally by scope / search). */
  getLabels: async (params?: { scopeId?: string; search?: string }): Promise<{ data: any[] }> => {
    const q = queryString.stringify({
      ...(params?.scopeId ? { scopeId: params.scopeId } : {}),
      ...(params?.search ? { search: params.search } : {}),
    });
    const { json } = await httpClient(`${apiUrl}/labels/labels${q ? `?${q}` : ''}`);
    return { data: json.data ?? [] };
  },

  createLabel: async (input: {
    scopeId?: string;
    value: string;
    color?: string;
    description?: string;
    ownerType: 'global' | 'group';
    ownerGroupId?: string;
  }): Promise<{ data: any }> => {
    const { json } = await httpClient(`${apiUrl}/labels/labels`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return { data: json.data ?? json };
  },

  updateLabel: async (
    id: string,
    input: { color?: string; description?: string }
  ): Promise<{ data: any }> => {
    const { json } = await httpClient(`${apiUrl}/labels/labels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return { data: json.data ?? json };
  },

  deleteLabel: async (id: string): Promise<void> => {
    await httpClient(`${apiUrl}/labels/labels/${id}`, { method: 'DELETE' });
  },

  /** GET assigned labels for a target (returns the labels themselves). */
  getTargetLabels: async (targetType: string, targetId: string): Promise<{ data: any[] }> => {
    const { json } = await httpClient(
      `${apiUrl}/labels/targets/${targetType}/${encodeURIComponent(targetId)}`
    );
    return { data: (json.data ?? []).map((a: any) => a.label) };
  },

  assignLabel: async (input: {
    labelId: string;
    targetType: string;
    targetId: string;
  }): Promise<{ data: any }> => {
    const { json } = await httpClient(`${apiUrl}/labels/assignments`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return { data: json.data ?? json };
  },

  unassignLabel: async (targetType: string, targetId: string, labelId: string): Promise<void> => {
    await httpClient(
      `${apiUrl}/labels/targets/${targetType}/${encodeURIComponent(targetId)}/${labelId}`,
      { method: 'DELETE' }
    );
  },

  // ============================================
  // HOTEL OPERATIONS REDESIGN — Llegadas / Reservas / Detalle / Pared (wave 2 UI)
  // ============================================

  /**
   * GET /api/v1/ui/arrivals — Llegadas de hoy (mockup 01). `hotelIds` (comma
   * list) or the legacy `hotelId`/`groupId` fallback, per useHotelScope().
   */
  getArrivals: async (
    params: ArrivalsQueryParams,
    signal?: AbortSignal
  ): Promise<{ data: ArrivalsResponse }> => {
    const query = buildArrivalsQuery(params);

    const url = `${apiUrl}/arrivals?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    const data = (json.data ?? json) as ArrivalsResponse;
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  /** POST /api/v1/ui/reservations/{id}/attention — acknowledge (✓). */
  acknowledgeReservation: async (id: string, acknowledged = true): Promise<{ data: any }> => {
    const { json } = await httpClient(`${apiUrl}/reservations/${id}/attention`, {
      method: 'POST',
      body: JSON.stringify({ acknowledged }),
    });
    return { data: json.data ?? json };
  },

  /** POST /api/v1/ui/reservations/{id}/attention — pin (📌). */
  pinReservation: async (id: string, pinned = true): Promise<{ data: any }> => {
    const { json } = await httpClient(`${apiUrl}/reservations/${id}/attention`, {
      method: 'POST',
      body: JSON.stringify({ pinned }),
    });
    return { data: json.data ?? json };
  },

  /** GET /api/v1/ui/reservations — Reservas (mockup 03), filtered-set summary. Several states or
   *  channels go as repeated parameters (`state=a&state=b`): the server reads each as one value. */
  getReservations: async (
    filter: ReservationsFilter,
    signal?: AbortSignal
  ): Promise<{ data: ReservationsResponse }> => {
    const url = `${apiUrl}/reservations?${reservationsQueryString(filter)}`;
    const { json } = await httpClient(url, { signal });
    const data = (json ?? {}) as ReservationsResponse;
    return { data: { ...data, data: DateTimeSystem.convertFromBackend(data.data ?? []) } };
  },

  /** GET /api/v1/ui/reservations/{id} — the six facts cards of mockup 04. */
  getReservationDetail: async (id: string): Promise<{ data: ReservationDetailResponse }> => {
    const url = `${apiUrl}/reservations/${id}`;
    const { json } = await httpClient(url);
    const data = (json.data ?? json) as ReservationDetailResponse;
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  /** GET /api/v1/ui/reservations/{id}/timeline — the guest's path, in hotel words. */
  getReservationTimeline: async (id: string): Promise<{ data: TimelineResponse }> => {
    const url = `${apiUrl}/reservations/${id}/timeline`;
    const { json } = await httpClient(url);
    const data = (json.data ?? json) as TimelineResponse;
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  /** GET /api/v1/ui/hotels/{id}/settings — used by the wall screen (mockup 02) and Opciones. */
  getHotelSettings: async (hotelId: string): Promise<{ data: HotelSettings }> => {
    const url = `${apiUrl}/hotels/${hotelId}/settings`;
    const { json } = await httpClient(url);
    return { data: (json.data ?? json) as HotelSettings };
  },

  /** PUT /api/v1/ui/hotels/{id}/settings — «Opciones del hotel» (mockup 13). */
  updateHotelSettings: async (hotelId: string, settings: HotelSettings): Promise<{ data: HotelSettings }> => {
    const { json } = await httpClient(`${apiUrl}/hotels/${hotelId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    return { data: (json.data ?? json) as HotelSettings };
  },

  /** POST /api/v1/ui/groups/{id}/settings/apply-to-hotels — GroupAdmin+ (mockup 13). */
  applyHotelSettingsToGroup: async (groupId: string, settings: HotelSettings): Promise<{ data: any }> => {
    const { json } = await httpClient(`${apiUrl}/groups/${groupId}/settings/apply-to-hotels`, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
    return { data: json.data ?? json };
  },

  /** GET /api/v1/ui/groups/{id}/settings — «Opciones de Roommatik» (mockup 13, Admin/SuperAdmin). */
  getGroupSettings: async (groupId: string): Promise<{ data: GroupSettings }> => {
    const url = `${apiUrl}/groups/${groupId}/settings`;
    const { json } = await httpClient(url);
    return { data: (json.data ?? json) as GroupSettings };
  },

  /** PUT /api/v1/ui/groups/{id}/settings. */
  updateGroupSettings: async (groupId: string, settings: GroupSettings): Promise<{ data: GroupSettings }> => {
    const { json } = await httpClient(`${apiUrl}/groups/${groupId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    return { data: (json.data ?? json) as GroupSettings };
  },

  // ============================================
  // HOTEL OPERATIONS REDESIGN — Dirección analytics (wave 3 UI)
  // ============================================

  /** GET /api/v1/ui/analytics/arrivals — Resumen (mockup 05). */
  getAnalyticsArrivals: async (
    params: { hotelIds?: string; hotelId?: string; groupId?: string; from?: string; to?: string; compare?: boolean; byHotel?: boolean },
    signal?: AbortSignal
  ): Promise<{ data: AnalyticsArrivalsResponse }> => {
    const query = buildSummaryQuery(params);
    const url = `${apiUrl}/analytics/arrivals?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    const data = (json.data ?? json) as AnalyticsArrivalsResponse;
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  /** GET /api/v1/ui/analytics/durations — Tiempos (mockup 07). */
  getAnalyticsDurations: async (
    params: { hotelIds?: string; hotelId?: string; groupId?: string; from?: string; to?: string; buckets?: 'standard' | 'fastKiosk' },
    signal?: AbortSignal
  ): Promise<{ data: DurationsResponse }> => {
    const query: Record<string, any> = {};
    if (params.hotelIds) query.hotelIds = params.hotelIds;
    if (params.hotelId) query.hotelId = params.hotelId;
    if (params.groupId) query.groupId = params.groupId;
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    if (params.buckets) query.buckets = params.buckets;

    const url = `${apiUrl}/analytics/durations?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    const data = (json.data ?? json) as DurationsResponse;
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  /** GET /api/v1/ui/analytics/payments — Cobros (mockup 08). Forbidden for Receptionist. */
  getAnalyticsPayments: async (
    params: { hotelIds?: string; hotelId?: string; groupId?: string; from?: string; to?: string; axis?: 'arrival' | 'payment' },
    signal?: AbortSignal
  ): Promise<{ data: PaymentsResponse }> => {
    const query: Record<string, any> = {};
    if (params.hotelIds) query.hotelIds = params.hotelIds;
    if (params.hotelId) query.hotelId = params.hotelId;
    if (params.groupId) query.groupId = params.groupId;
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    if (params.axis) query.axis = params.axis;

    const url = `${apiUrl}/analytics/payments?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    const data = (json.data ?? json) as PaymentsResponse;
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  /** GET /api/v1/ui/analytics/hotels — Hoteles (mockup 09). */
  getAnalyticsHotels: async (
    params: { hotelIds?: string; groupId?: string; from?: string; to?: string; onlyWithArrivals?: boolean },
    signal?: AbortSignal
  ): Promise<{ data: HotelsOverviewResponse }> => {
    const query: Record<string, any> = {};
    if (params.hotelIds) query.hotelIds = params.hotelIds;
    if (params.groupId) query.groupId = params.groupId;
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    if (params.onlyWithArrivals) query.onlyWithArrivals = true;

    const url = `${apiUrl}/analytics/hotels?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    const data = (json.data ?? json) as HotelsOverviewResponse;
    return { data: DateTimeSystem.convertFromBackend(data) };
  },

  // ============================================
  // DISPENSADORES — the production dashboard's dispenser block, as its own
  // Dirección screen. Same endpoints and parameters as before, plus the
  // `hotelIds` scope every v1 analytics endpoint accepts (Decision 6).
  // ============================================

  /** GET /api/v1/ui/analytics/dispensers/summary — keys, success rate, dispensers, errors by code. */
  getDispenserSummary: async (params: DispenserQuery, signal?: AbortSignal): Promise<{ data: DispenserSummary }> => {
    const { json } = await httpClient(`${apiUrl}/analytics/dispensers/summary?${queryString.stringify(params)}`, { signal });
    return { data: (json.data ?? json) as DispenserSummary };
  },

  /** GET /api/v1/ui/analytics/dispensers/by-hotel — per-hotel deliveries, ranked by keys delivered. */
  getDispenserByHotel: async (params: DispenserQuery, signal?: AbortSignal): Promise<{ data: DispenserByHotelResponse }> => {
    const { json } = await httpClient(`${apiUrl}/analytics/dispensers/by-hotel?${queryString.stringify(params)}`, { signal });
    return { data: (json.data ?? json) as DispenserByHotelResponse };
  },

  /** GET /api/v1/ui/analytics/dispensers/daily-trend — deliveries and failures per day. */
  getDispenserDailyTrend: async (params: DispenserQuery, signal?: AbortSignal): Promise<{ data: DispenserDailyTrendResponse }> => {
    const { json } = await httpClient(`${apiUrl}/analytics/dispensers/daily-trend?${queryString.stringify(params)}`, { signal });
    return { data: (json.data ?? json) as DispenserDailyTrendResponse };
  },

  // ============================================
  // INSIGHT V2 — property list
  // ============================================

  /** GET /api/v2/ui/properties — the v2 properties the current user may view,
   *  ordered by display name. Every v2 screen selects its `property` from
   *  this list; the v1 hotel list is a different id space. */
  getAnalyticsV2Properties: async (signal?: AbortSignal): Promise<{ data: V2Property[] }> => {
    const { json } = await httpClient(`${apiUrlV2}/properties`, { signal });
    return { data: (json.data ?? json) as V2Property[] };
  },

  // ============================================
  // INSIGHT V2 — Operación (w7.u2), v2 read API (w7.u1)
  // ============================================

  /** GET /api/v2/ui/operacion — the Operación page (CF S6): the five director
   *  cards plus every registered `ops.*` id as supporting panels, for one
   *  property. Not run through `DateTimeSystem.convertFromBackend` — its date-
   *  field heuristic matches on substrings like `date`/`updated` that a
   *  breakdown panel's own keys (`ops.arrivals_by_end_state`'s states, etc.)
   *  could collide with; `stamp.freshnessAsOf` is parsed directly instead
   *  (`MetricStamp`, `DateTimeSystem.formatRelative` already accepts a raw
   *  ISO string). */
  getAnalyticsOperacion: async (
    params: { property: string; from: string; to: string },
    signal?: AbortSignal
  ): Promise<{ data: OperacionResponse }> => {
    const query = queryString.stringify({ property: params.property, from: params.from, to: params.to });
    const url = `${apiUrlV2}/operacion?${query}`;
    const { json } = await httpClient(url, { signal });
    return { data: (json.data ?? json) as OperacionResponse };
  },

  // ============================================
  // INSIGHT V2 — Live board (w7.u3), v2 read API (w7.u1)
  // ============================================

  /** GET /api/v2/ui/board?property= — the reception live board (CF S6): a
   *  flat list of `board.*` ids for one property, always today's business
   *  date (no period picker — V2UiBoardController.cs). Not run through
   *  `DateTimeSystem.convertFromBackend` — same reason as `getAnalyticsOperacion`
   *  (a row's own keys, e.g. `opened_at`/`acknowledged_at`, could collide with
   *  its date-field substring heuristic); rows are read directly as ISO
   *  strings by the components that render them. */
  getAnalyticsBoard: async (
    params: { property: string },
    signal?: AbortSignal
  ): Promise<{ data: BoardResponse }> => {
    const query = queryString.stringify({ property: params.property });
    const url = `${apiUrlV2}/board?${query}`;
    const { json } = await httpClient(url, { signal });
    return { data: (json.data ?? json) as BoardResponse };
  },

  // ============================================
  // INSIGHT V2 — Reconciliation, Control, Status, Benchmark, Fit (w7.u5)
  // v2 read API (w7.u1). Not run through `DateTimeSystem.convertFromBackend`
  // (same reason as `getAnalyticsOperacion`: a breakdown panel's own keys can
  // collide with its date-field heuristic) — `stamp.freshnessAsOf` is parsed
  // directly by `MetricStamp` instead.
  // ============================================

  /** GET /api/v2/ui/reconciliation — `fin.*` ids for one property/period. */
  getAnalyticsReconciliation: async (
    params: { property: string; from: string; to: string },
    signal?: AbortSignal
  ): Promise<{ data: MetricResultDto[] }> => {
    const query = queryString.stringify({ property: params.property, from: params.from, to: params.to });
    const url = `${apiUrlV2}/reconciliation?${query}`;
    const { json } = await httpClient(url, { signal });
    return { data: (json.data ?? json) as MetricResultDto[] };
  },

  /** GET /api/v2/ui/control — `it.*` ids for one property/period. */
  getAnalyticsControl: async (
    params: { property: string; from: string; to: string },
    signal?: AbortSignal
  ): Promise<{ data: MetricResultDto[] }> => {
    const query = queryString.stringify({ property: params.property, from: params.from, to: params.to });
    const url = `${apiUrlV2}/control?${query}`;
    const { json } = await httpClient(url, { signal });
    return { data: (json.data ?? json) as MetricResultDto[] };
  },

  /** GET /api/v2/ui/status — per-source ingest gap register for one property
   *  (window defaults to the trailing 24h server-side when omitted). */
  getAnalyticsStatus: async (
    params: { property: string; from?: string; to?: string },
    signal?: AbortSignal
  ): Promise<{ data: StatusResponse }> => {
    const query: Record<string, any> = { property: params.property };
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    const url = `${apiUrlV2}/status?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    return { data: (json.data ?? json) as StatusResponse };
  },

  /** GET /api/v2/ui/benchmark — `chain.ranking`/`chain.median`/
   *  `chain.properties_with_rates` for the property's own chain proxy,
   *  narrowed by the API to the caller's own hotel when `scope` is `own`. */
  getAnalyticsBenchmark: async (
    params: { property: string; from: string; to: string },
    signal?: AbortSignal
  ): Promise<{ data: BenchmarkResponse }> => {
    const query = queryString.stringify({ property: params.property, from: params.from, to: params.to });
    const url = `${apiUrlV2}/benchmark?${query}`;
    const { json } = await httpClient(url, { signal });
    return { data: parseBenchmarkResponse(json) };
  },

  /** GET /api/v2/ui/fit — `fit.*` ids over the de-identified solution-fitness
   *  projection (product C, D25: Roommatik product team only), for one
   *  property/period and an optional cut (an omitted cut means every value
   *  of that dimension together). */
  getAnalyticsFit: async (
    params: { property: string; from: string; to: string; appVersion?: string; flowConfigRef?: string; language?: string },
    signal?: AbortSignal
  ): Promise<{ data: MetricResultDto[] }> => {
    const query: Record<string, any> = { property: params.property, from: params.from, to: params.to };
    if (params.appVersion) query.appVersion = params.appVersion;
    if (params.flowConfigRef) query.flowConfigRef = params.flowConfigRef;
    if (params.language) query.language = params.language;
    const url = `${apiUrlV2}/fit?${queryString.stringify(query)}`;
    const { json } = await httpClient(url, { signal });
    return { data: (json.data ?? json) as MetricResultDto[] };
  },
  // ============================================
  // INSIGHT V2 — Satisfacción (satisfaction survey)
  // ============================================

  /** GET /api/v2/ui/survey/summary — the survey figures of one property and
   *  period, optionally one channel. Means are null when nobody answered. */
  getSurveySummary: async (
    params: { property: string; from: string; to: string; channel?: SurveyChannel },
    signal?: AbortSignal
  ): Promise<{ data: SurveySummaryResponse }> => {
    const query: Record<string, any> = { property: params.property, from: params.from, to: params.to };
    if (params.channel) query.channel = params.channel;
    const { json } = await httpClient(`${apiUrlV2}/survey/summary?${queryString.stringify(query)}`, { signal });
    return { data: (json.data ?? json) as SurveySummaryResponse };
  },

  /** GET /api/v2/ui/survey/responses — one row per journey, newest first,
   *  1-based pages of at most 100. Rows carry the guest's free comment. */
  getSurveyResponses: async (
    params: { property: string; from: string; to: string; channel?: SurveyChannel; withComment?: boolean; page: number; pageSize: number },
    signal?: AbortSignal
  ): Promise<{ data: SurveyResponsesPage }> => {
    const query: Record<string, any> = {
      property: params.property,
      from: params.from,
      to: params.to,
      page: params.page,
      pageSize: params.pageSize,
    };
    if (params.channel) query.channel = params.channel;
    if (params.withComment) query.withComment = true;
    const { json } = await httpClient(`${apiUrlV2}/survey/responses?${queryString.stringify(query)}`, { signal });
    return { data: (json.data ?? json) as SurveyResponsesPage };
  },

  /** GET /api/v2/ui/ask/status — whether «Preguntar» is offered to this person. */
  getAskStatus: async (signal?: AbortSignal): Promise<{ data: AskStatus }> => {
    const { json } = await httpClient(`${apiUrlV2}/ask/status`, { signal });
    return { data: (json.data ?? json) as AskStatus };
  },

  /** POST /api/v2/ui/ask — one question, with the previous turns of the conversation. */
  ask: async (params: { question: string; history: AskTurn[]; view?: AskView | null }, signal?: AbortSignal): Promise<{ data: AskResponse }> => {
    const { json } = await httpClient(`${apiUrlV2}/ask`, {
      method: 'POST',
      body: JSON.stringify({ question: params.question, history: params.history, view: params.view ?? null }),
      signal,
    });
    return { data: (json.data ?? json) as AskResponse };
  },

  /** GET /api/v1/ui/release-notes/pending — the notes this person has not acknowledged yet (usually none). */
  getPendingReleaseNotes: async (lang: ManualLang): Promise<ReleaseNote[]> => {
    const { json } = await httpClient(`${apiUrl}/release-notes/pending?lang=${lang}`);
    return (json.data ?? json) as ReleaseNote[];
  },

  /** GET /api/v1/ui/release-notes — every note published for this person, newest first, with `seen`. */
  getReleaseNotes: async (lang: ManualLang): Promise<ReleaseNote[]> => {
    const { json } = await httpClient(`${apiUrl}/release-notes?lang=${lang}`);
    return (json.data ?? json) as ReleaseNote[];
  },

  /** POST /api/v1/ui/release-notes/seen — acknowledges the given notes for this person. */
  markReleaseNotesSeen: async (ids: string[]): Promise<void> => {
    await httpClient(`${apiUrl}/release-notes/seen`, { method: 'POST', body: JSON.stringify({ ids }) });
  },

  /** GET /api/v1/ui/manual/index — parts, chapters and screen links of the
   *  caller's edition of the user manual (staff text is cut server-side). */
  getManualIndex: async (lang: ManualLang): Promise<ManualIndex> => {
    const { json } = await httpClient(`${apiUrl}/manual/index?lang=${lang}`);
    return (json.data ?? json) as ManualIndex;
  },

  /** GET /api/v1/ui/manual/chapter/{id} — one chapter of the caller's edition. */
  getManualChapter: async (id: string, lang: ManualLang): Promise<ManualChapter> => {
    const { json } = await httpClient(`${apiUrl}/manual/chapter/${encodeURIComponent(id)}?lang=${lang}`);
    return (json.data ?? json) as ManualChapter;
  },

  /** GET /api/v1/ui/manual/search — the caller's edition only (customers never get staff text). */
  searchManual: async (query: string, lang: ManualLang): Promise<ManualSearchResponse> => {
    const { json } = await httpClient(`${apiUrl}/manual/search?q=${encodeURIComponent(query)}&lang=${lang}`);
    return (json.data ?? json) as ManualSearchResponse;
  },

  /** GET /api/v1/ui/manual/images/{id} — behind sign-in, so fetched as a blob
   *  rather than used as an `<img src>`. `imageId` is `<name>-<lang>`. */
  getManualImage: async (imageId: string): Promise<Blob> => {
    const response = await authorizedFetch(`${apiUrl}/manual/images/${encodeURIComponent(imageId)}`);
    if (!response.ok) {
      const error: any = new Error(response.statusText);
      error.status = response.status;
      throw error;
    }
    return response.blob();
  },
};

export default extendedDataProvider;